/**
 * Page 2 of Link Artifact Drawer
 * Add aliases to collection version
 */
import {TagType} from '@wandb/weave/common/components/Tags';
// @todo: Migrate to 'Icon' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Icon} from '@wandb/weave/components/Icon';
import _ from 'lodash';
import React, {useEffect, useState} from 'react';
import {MultiValue} from 'react-select';

import {RE_VERSION} from '../../../../../util/artifacts';
import {capitalizeFirstLetter} from '../../../../../util/string';
import {ArtifactVersionTTLBanner} from '../ArtifactVersionTTLBanner';
// @todo: Migrate to 'Select' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {CreatableSelect} from '../CreatableSelect';
import {Artifact, Collection} from '../CreateRegisteredCollectionDrawerInner';
import * as S from '../CreateRegisteredCollectionDrawerInner.styles';
import {SubText} from '../SubText';

type AliasOptionType = {
  value: string;
  label: string;
};

type DrawerPageLinkVersionProps = {
  collection: Collection;
  artifact: Artifact;
  aliasList: string[];
  isInvalidAliasList?: boolean;
  viewerIsRegistryAdmin: boolean;
  protectedAliases: string[];
  setAliasList: (val: string[]) => void;
  setIsInvalidAliasList?: (val: boolean) => void;
};

export const DrawerPageLinkVersion = ({
  collection,
  artifact,
  aliasList,
  setAliasList,
  isInvalidAliasList,
  setIsInvalidAliasList,
  viewerIsRegistryAdmin,
  protectedAliases,
}: DrawerPageLinkVersionProps) => {
  const currentAliasesOptions =
    collection.aliases?.map(alias => {
      return {
        value: alias,
        label: alias,
      };
    }) ?? [];

  // Alias list validation
  // Version aliases are not allowed
  // Protected aliases are allowed only with the right permissions
  const [hasVersionAlias, setHasVersionAlias] = useState(false);
  const [hasInvalidProtectedAlias, setHasInvalidProtectedAlias] =
    useState(false);
  const [invalidProtectedAliases, setInvalidProtectedAliases] = useState<
    string[]
  >([]);

  useEffect(() => {
    const versionRegExp = new RegExp(RE_VERSION);
    const containsVersionAlias = _.some(aliasList, str =>
      versionRegExp.test(str)
    );
    setHasVersionAlias(containsVersionAlias);

    const includedProtectedAliases =
      protectedAliases?.filter(alias => aliasList.includes(alias)) ?? [];
    const containsProtectedAlias = includedProtectedAliases.length > 0;
    const containsInvalidProtectedAlias =
      !viewerIsRegistryAdmin && containsProtectedAlias;

    if (containsInvalidProtectedAlias) {
      setHasInvalidProtectedAlias(true);
      setInvalidProtectedAliases(includedProtectedAliases);
    } else {
      setHasInvalidProtectedAlias(false);
      setInvalidProtectedAliases([]);
    }
    if (!containsVersionAlias && !containsInvalidProtectedAlias) {
      setIsInvalidAliasList?.(false);
    } else {
      setIsInvalidAliasList?.(true);
    }
  }, [
    aliasList,
    setHasVersionAlias,
    setIsInvalidAliasList,
    protectedAliases,
    viewerIsRegistryAdmin,
    setHasInvalidProtectedAlias,
    setInvalidProtectedAliases,
  ]);
  return (
    <>
      <S.PageLabelRow>
        Step 2 of 2 - {capitalizeFirstLetter(collection.artifactTypeName)}{' '}
        version details
      </S.PageLabelRow>
      <S.LabelRow>
        <S.Label>Selected {collection.artifactTypeName} artifact</S.Label>
      </S.LabelRow>
      <S.ArtifactNamePanel>
        {artifact.sequenceName}(v{artifact.versionIndex})
      </S.ArtifactNamePanel>
      <S.LinkCollectionNamePanel>
        <Icon name="versions-layers" style={{marginRight: '4px'}} />
        Registry version - {collection.name}:v{collection.numVersions}
      </S.LinkCollectionNamePanel>
      <S.LabelRow>
        <S.Label>Aliases</S.Label>
      </S.LabelRow>
      <div data-test="aliases-input">
        <div>
          <CreatableSelect<AliasOptionType, true>
            options={currentAliasesOptions}
            tagType={TagType.ALIAS}
            errorState={isInvalidAliasList}
            onChange={(options: MultiValue<AliasOptionType>, actionMeta: any) =>
              setAliasList(options.map(option => option.value))
            }
            isMulti={true}
            value={aliasList?.map(alias => {
              return {
                value: alias,
                label: alias,
              };
            })}
            onInputChange={(value: string) => {
              // alias validation
              const regExp = /[^-\w]/gi;
              const reValidString = new RegExp(RE_VERSION);
              if (!reValidString.test(value)) {
                return value.replace(regExp, '');
              }
              return value;
            }}
            placeholder="Select or create aliases..."
          />
        </div>
        {hasVersionAlias && (
          <SubText isError={true}>
            Version aliases are added automatically, and cannot be customized.
            Please remove it to proceed.
          </SubText>
        )}
        {hasInvalidProtectedAlias && (
          <SubText isError={true}>
            {getAliasAlertFromList(invalidProtectedAliases)}
          </SubText>
        )}
      </div>
      <ArtifactVersionTTLBanner artifact={artifact} />
    </>
  );
};

/**
 * Util function to convert list of alias strings
 * to alert text
 */
function getAliasAlertFromList(aliases: string[]) {
  const aliasesWithQuotes = aliases.map(alias => `"${alias}"`);
  if (aliasesWithQuotes.length === 1) {
    return `The alias ${aliasesWithQuotes[0]} is protected, and can only be added by an Admin. Please remove it to proceed.`;
  }
  return `The aliases ${aliasesWithQuotes.join(
    ', '
  )} are protected, and can only be added by an Admin. Please remove them to proceed.`;
}
